// ========================================
// PAYROLL PERIOD FIX - HELPER FUNCTIONS
// Issue: Half-monthly salary generated twice without period tracking
// Solution: Separate batch creation for Monthly vs Half-monthly employees
// ========================================

import axios from 'axios';
import { toast } from 'react-toastify';
import fetchData from '../functions/fetchData';
import { getHeaders } from '../utility/getHeader';

/**
 * 1. Determine Payroll Period based on salary type and date
 * @param {string} salaryType - Employee salary type
 * @param {Date|null} specificDate - Optional specific date to check
 * @returns {string} - 'full-month', '1-15', or '16-30'
 */
export const getPayrollPeriod = (salaryType, specificDate = null) => {
  const normalized = String(salaryType || '').toLowerCase().replace(/[\s_-]/g, '');
  
  if (normalized === 'halfmonthly') {
    // For half-monthly, determine if 1-15 or 16-30 based on current/provided date
    const dateToCheck = specificDate ? new Date(specificDate) : new Date();
    const dayOfMonth = dateToCheck.getDate();
    
    if (dayOfMonth <= 15) {
      return '1-15';
    } else {
      return '16-30';
    }
  }
  
  // Monthly employees always get full-month
  return 'full-month';
};

/**
 * 2. Get start and end dates for a payroll period
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {string} period - 'full-month', '1-15', or '16-30'
 * @returns {Object} - {startDate, endDate}
 */
export const getPeriodDates = (year, month, period) => {
  const startDate = new Date(year, month - 1, 1);
  let endDate;
  
  if (period === '1-15') {
    endDate = new Date(year, month - 1, 15);
  } else if (period === '16-30') {
    const nextMonth = new Date(year, month, 1);
    endDate = new Date(nextMonth.getTime() - 1);
  } else {
    endDate = new Date(year, month, 0); // Last day of month
  }
  
  return { 
    startDate: startDate.toISOString().split('T')[0], 
    endDate: endDate.toISOString().split('T')[0] 
  };
};

/**
 * 3. Check for duplicate payroll entries for a specific period
 * @param {number} departmentId - Department ID
 * @param {string} monthYear - Month in YYYY-MM format
 * @param {string} period - 'full-month', '1-15', or '16-30'
 * @returns {Promise<Array>} - Array of existing payroll records (empty if none exist)
 */
export const checkForDuplicatePayroll = async (departmentId, monthYear, period) => {
  try {
    const [year, month] = monthYear.split('-');
    
    // Use axios.get for query parameters
    const response = await axios.get(
      `/hr/check-duplicate?department_id=${departmentId}&month=${parseInt(month, 10)}&year=${year}&period=${period}`,
      getHeaders()
    );
    
    // Backend returns { success, all_records, duplicates, summary }
    // We return all_records to indicate existing payroll for this period
    if (response.data && response.data.success) {
      const existingRecords = response.data.all_records || [];
      
      if (existingRecords.length > 0) {
        console.log(`🔍 Found ${existingRecords.length} existing payroll records for ${period}:`, response.data.summary);
      }
      
      return existingRecords;
    }
    
    return [];
  } catch (error) {
    console.error('Error checking for duplicate payroll:', error);
    // Return empty array on error to allow creation attempt
    // Backend will enforce constraints if duplicates exist
    return [];
  }
};

/**
 * 4. Create payroll batches separated by salary type
 * @param {number} departmentId - Department ID
 * @param {string} monthYear - Month in YYYY-MM format
 * @param {Array} employeeList - List of employees
 * @returns {Promise<Array>} - Array of batch definitions to create
 */
export const createPayrollBatchesForDepartment = async (
  departmentId,
  monthYear,
  employeeList
) => {
  try {
    const [year, month] = monthYear.split('-');
    
    // Separate employees by salary type
    const monthlyEmployees = employeeList.filter(emp => {
      const normalized = String(emp.salaryType || emp.salary_type || '').toLowerCase().replace(/[\s_-]/g, '');
      return normalized !== 'halfmonthly';
    });
    
    const halfMonthlyEmployees = employeeList.filter(emp => {
      const normalized = String(emp.salaryType || emp.salary_type || '').toLowerCase().replace(/[\s_-]/g, '');
      return normalized === 'halfmonthly';
    });
    
    console.log('👥 Employee Separation:');
    console.log('  Monthly employees:', monthlyEmployees.length);
    console.log('  Half-monthly employees:', halfMonthlyEmployees.length);
    
    const batchesToCreate = [];
    
    // ========================================
    // Create batch for MONTHLY employees
    // ========================================
    if (monthlyEmployees.length > 0) {
      // Check if already exists
      const existingMonthlyBatch = await checkForDuplicatePayroll(
        departmentId,
        monthYear,
        'full-month'
      );
      
      if (existingMonthlyBatch.length === 0) {
        const { startDate, endDate } = getPeriodDates(parseInt(year), parseInt(month), 'full-month');
        
        batchesToCreate.push({
          departmentId: departmentId,
          batchType: 'monthly',
          period: 'full-month',
          periodLabel: 'Full Month',
          period_start_date: startDate,
          period_end_date: endDate,
          employees: monthlyEmployees,
          employeeCount: monthlyEmployees.length
        });
        
        console.log('✅ Monthly batch will be created');
      } else {
        toast.warning(`Monthly payroll batch already exists for ${monthYear}. Skipping...`, {
          position: 'top-right',
          autoClose: 4000
        });
        console.log('⚠️ Monthly batch already exists - skipping');
      }
    }
    
    // ========================================
    // Create batches for HALF-MONTHLY employees
    // ========================================
    if (halfMonthlyEmployees.length > 0) {
      // Check for 1-15 batch
      const existing_1_15 = await checkForDuplicatePayroll(
        departmentId,
        monthYear,
        '1-15'
      );
      
      if (existing_1_15.length === 0) {
        const { startDate, endDate } = getPeriodDates(parseInt(year), parseInt(month), '1-15');
        
        batchesToCreate.push({
          departmentId: departmentId,
          batchType: 'half-monthly',
          period: '1-15',
          periodLabel: '1st - 15th (Half)',
          period_start_date: startDate,
          period_end_date: endDate,
          employees: halfMonthlyEmployees,
          employeeCount: halfMonthlyEmployees.length
        });
        
        console.log('✅ Half-monthly batch (1-15) will be created');
      } else {
        toast.warning(`Half-monthly batch (1-15) already exists for ${monthYear}. Skipping...`, {
          position: 'top-right',
          autoClose: 4000
        });
        console.log('⚠️ Half-monthly batch (1-15) already exists - skipping');
      }
      
      // Check for 16-30 batch
      const existing_16_30 = await checkForDuplicatePayroll(
        departmentId,
        monthYear,
        '16-30'
      );
      
      if (existing_16_30.length === 0) {
        const { startDate, endDate } = getPeriodDates(parseInt(year), parseInt(month), '16-30');
        
        batchesToCreate.push({
          departmentId: departmentId,
          batchType: 'half-monthly',
          period: '16-30',
          periodLabel: '16th - Last (Half)',
          period_start_date: startDate,
          period_end_date: endDate,
          employees: halfMonthlyEmployees,
          employeeCount: halfMonthlyEmployees.length
        });
        
        console.log('✅ Half-monthly batch (16-30) will be created');
      } else {
        toast.warning(`Half-monthly batch (16-30) already exists for ${monthYear}. Skipping...`, {
          position: 'top-right',
          autoClose: 4000
        });
        console.log('⚠️ Half-monthly batch (16-30) already exists - skipping');
      }
    }
    
    console.log('📦 Total batches to create:', batchesToCreate.length);
    
    // Return array directly for easier consumption
    return batchesToCreate;
    
  } catch (error) {
    console.error('Error creating payroll batches:', error);
    toast.error('Failed to create payroll batches: ' + error.message);
    return [];
  }
};

/**
 * 5. Calculate salary for a specific payroll period
 * @param {Object} employeeData - Employee data
 * @param {string} payrollPeriod - 'full-month', '1-15', or '16-30'
 * @param {Object} salaryData - Salary and deduction data
 * @returns {Object} - {gross_salary, total_deductions, net_salary}
 */
export const calculateSalaryForPeriod = (employeeData, payrollPeriod, salaryData) => {
  const {
    monthly_salary = 0,
    sso_deduction = 0,
    not_checkout_penalty = 0,
    not_checkin_penalty = 0,
    late_penalty = 0,
    pending_deduction = 0,
    cut_deduction = 0,
    advance_deduction = 0,
    bonus = 0,
    overtime = 0,
    other_income = 0,
    absent_deduction = 0
  } = salaryData;
  
  let gross_salary;
  let total_deductions;
  
  if (payrollPeriod === 'full-month') {
    // Full monthly salary
    gross_salary = monthly_salary + bonus + overtime + other_income;
    total_deductions = sso_deduction + not_checkout_penalty + not_checkin_penalty + 
                      late_penalty + pending_deduction + cut_deduction + advance_deduction + absent_deduction;
  } else {
    // Half-monthly salary - split everything in half
    gross_salary = (monthly_salary / 2) + (bonus / 2) + (overtime / 2) + (other_income / 2);
    total_deductions = (sso_deduction / 2) + (not_checkout_penalty / 2) + (not_checkin_penalty / 2) + 
                      (late_penalty / 2) + (pending_deduction / 2) + (cut_deduction / 2) + 
                      (advance_deduction / 2) + (absent_deduction / 2);
  }
  
  const net_salary = gross_salary - total_deductions;
  
  return {
    gross_salary: Math.round(gross_salary * 100) / 100,
    total_deductions: Math.round(total_deductions * 100) / 100,
    net_salary: Math.round(net_salary * 100) / 100
  };
};

/**
 * 6. Format period label for display
 * @param {string} period - 'full-month', '1-15', or '16-30'
 * @returns {string} - Formatted label
 */
export const formatPeriodLabel = (period) => {
  switch (period) {
    case '1-15':
      return 'Half-Month 1-15';
    case '16-30':
      return 'Half-Month 16-30/31';
    case 'full-month':
      return 'Full Month';
    default:
      return period;
  }
};

/**
 * 7. Get period badge color
 * @param {string} period - 'full-month', '1-15', or '16-30'
 * @returns {string} - Color class
 */
export const getPeriodBadgeColor = (period) => {
  switch (period) {
    case '1-15':
      return 'primary';
    case '16-30':
      return 'success';
    case 'full-month':
      return 'warning';
    default:
      return 'secondary';
  }
};
